/**
 * AgentLauncher — global ⌘K (Ctrl+K on Windows/Linux) command palette
 * to fuzzy-find and launch any registered AI agent from anywhere in the app.
 *
 * Driven by `ALL_AGENTS` in /app/frontend/src/config/agentRegistry.js — adding
 * a new sub-module's agent there (or to its source array) makes it instantly
 * discoverable in the launcher.
 *
 * Behaviour:
 *   • Cmd/Ctrl+K toggles the dialog from anywhere except inside an <input>.
 *   • Selecting an agent opens a right-side <Sheet> with the live chat panel.
 *   • The sheet also has "Open module" — deep-links to the sub-module page.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandShortcut,
} from '@/components/ui/command';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Bot, ExternalLink, Sparkles } from 'lucide-react';
import { SourcingAgentPanel } from '@/components/sourcing/SourcingL2Kit';
import { ALL_AGENTS, SUITE_ORDER } from '@/config/agentRegistry';

const SUITE_TONE = {
  'Sourcing':            'bg-violet-100 text-violet-700 border-violet-200',
  'Category & Spend':    'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Supplier Management': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Requisitions':        'bg-sky-100 text-sky-700 border-sky-200',
};

export default function AgentLauncher() {
  const [open, setOpen] = useState(false);
  const [activeAgent, setActiveAgent] = useState(null);
  const navigate = useNavigate();

  // Group agents by suite (in canonical order)
  const grouped = useMemo(() => {
    const out = {};
    SUITE_ORDER.forEach(s => { out[s] = []; });
    ALL_AGENTS.forEach(a => {
      if (!out[a.suite]) out[a.suite] = [];
      out[a.suite].push(a);
    });
    return out;
  }, []);

  // Global ⌘K / Ctrl+K hotkey
  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key && e.key.toLowerCase() === 'k';
      if (!isK || !(e.metaKey || e.ctrlKey)) return;
      // Skip when typing into a form control (let the OS-native shortcut win)
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) {
        // still allow if alt is held — power-user override
        if (!e.altKey) return;
      }
      e.preventDefault();
      setOpen(o => !o);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onPick = (agent) => {
    setOpen(false);
    setActiveAgent(agent);
  };

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search 39 AI agents — by name, sub-module code, or capability…"
                      data-testid="agent-launcher-input"/>
        <CommandList data-testid="agent-launcher-list">
          <CommandEmpty>No agent matches. Try "kraljic", "BAFO", "RQ-5.6"…</CommandEmpty>
          <div className="px-3 py-2 text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-1.5 border-b">
            <Sparkles className="w-3 h-3 text-violet-500"/>
            {ALL_AGENTS.length} agents · Press ⌘K anywhere to reopen
          </div>
          {SUITE_ORDER.map(suite => {
            const items = grouped[suite] || [];
            if (!items.length) return null;
            return (
              <CommandGroup key={suite} heading={suite}>
                {items.map(a => {
                  const Icon = a.Icon || Bot;
                  return (
                    <CommandItem key={a.module}
                      value={`${a.module} ${a.code} ${a.name} ${a.label} ${a.suite}`}
                      onSelect={() => onPick(a)}
                      data-testid={`agent-launcher-item-${a.module}`}>
                      <span className={`w-7 h-7 rounded-full bg-${a.tone}-100 text-${a.tone}-700 flex items-center justify-center shrink-0`}>
                        <Icon className="w-3.5 h-3.5"/>
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{a.name}</div>
                        <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                          <span className="font-mono">{a.code}</span>
                          <span className="text-slate-300">·</span>
                          <span className="truncate">{a.label}</span>
                        </div>
                      </div>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border ${SUITE_TONE[suite] || ''}`}>{suite}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            );
          })}
        </CommandList>
      </CommandDialog>

      <Sheet open={!!activeAgent} onOpenChange={(o) => !o && setActiveAgent(null)}>
        <SheetContent side="right"
          className="w-full sm:max-w-[640px] overflow-y-auto bg-gradient-to-b from-white to-slate-50"
          data-testid="agent-launcher-drawer">
          {activeAgent && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 text-base">
                  <Bot className="w-4 h-4 text-violet-600"/>
                  <span>{activeAgent.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${SUITE_TONE[activeAgent.suite] || ''}`}>
                    {activeAgent.code}
                  </span>
                </SheetTitle>
                <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
                  <span>{activeAgent.suite} · {activeAgent.label}</span>
                  {activeAgent.route && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs"
                      onClick={() => { setActiveAgent(null); navigate(activeAgent.route); }}
                      data-testid="agent-launcher-open-module">
                      <ExternalLink className="w-3 h-3 mr-1"/>Open module
                    </Button>
                  )}
                </div>
              </SheetHeader>
              <div className="mt-3">
                <SourcingAgentPanel
                  moduleKey={activeAgent.module}
                  agentName={activeAgent.name}
                  contextData={{ source: 'agent-launcher', stage: activeAgent.code }}
                  defaultOpen
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
